/**
 * Register.jsx — CyberExchange Premium User Registration Page.
 *
 * Split-screen layout:
 * - Left panel: Glowing abstract security grid overlay and core value statements.
 * - Right panel: Sleek, interactive glassmorphic register card.
 * Features:
 * - Clean SVG vector icons instead of raw emojis for a professional enterprise feel.
 * - Concise copy and highly readable bright slate text elements.
 * - Role selection via custom card items (Buyer vs Vendor).
 * - Styled custom input fields matching global design system.
 * - Live state-driven password field visibility toggles.
 * - Comprehensive client validation and backend API error matching.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, persistSessionMetadata, setTokens } from '../utils/auth';

// ── Sleek SVG Outline Icons ──────────────────────────────────────────────────
const ShieldIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const UserIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const BriefcaseIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

const ScaleIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1"/>
    <path d="M18 8h4a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4"/>
    <path d="M16 12h8"/>
  </svg>
);

const MailIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const LockIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const EyeIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const CheckIcon = ({ className = '', size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const AlertIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const GlobeIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const ToolIcon = ({ className = '', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>
);

const ERROR_MESSAGES = {
  EMAIL_ALREADY_EXISTS:
    'An account with this email address already exists. Please log in instead.',
  CONSENT_REQUIRED:
    'You must accept the DPDP consent to create an account.',
  IDENTITY_PERMANENTLY_BANNED:
    'Registration is not permitted for this identity. Please contact support at support@cyberexchange.in.',
  VALIDATION_ERROR:
    'Please check the form for errors and try again.',
  REGISTRATION_FAILED:
    'Registration failed due to a server error. Please try again in a moment.',
  RATE_LIMITED:
    'Too many registration attempts. Please wait a minute and try again.',
};

function getErrorMessage(err) {
  const status = err.response?.status;
  const code = err.response?.data?.error?.code;
  const serverMessage = err.response?.data?.error?.message;

  if (status === 429) return ERROR_MESSAGES.RATE_LIMITED;
  if (!err.response) return 'Unable to connect to the server. Please check your internet connection.';
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  if (serverMessage) return serverMessage;
  return 'Registration failed. Please try again.';
}

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'BUYER',
    mobile: '',
    pan_number: '',
    gst_number: '',
    dpdp_consent: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError('');
  };

  const selectRole = (role) => {
    setForm((prev) => ({ ...prev, role }));
    setFieldErrors((prev) => ({ ...prev, role: '' }));
  };

  const validate = () => {
    const errs = {};

    if (!form.email.trim()) {
      errs.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Please enter a valid email address (e.g. user@example.com).';
    }

    if (!form.password) {
      errs.password = 'Password is required.';
    } else if (form.password.length < 8) {
      errs.password = 'Password must be at least 8 characters long.';
    } else if (!/[A-Z]/.test(form.password)) {
      errs.password = 'Password must contain at least one uppercase letter.';
    } else if (!/[0-9]/.test(form.password)) {
      errs.password = 'Password must contain at least one number.';
    }

    if (!form.confirmPassword) {
      errs.confirmPassword = 'Please confirm your password.';
    } else if (form.password !== form.confirmPassword) {
      errs.confirmPassword = 'Passwords do not match.';
    }

    if (!form.dpdp_consent) {
      errs.dpdp_consent = 'You must accept the DPDP consent to register.';
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }

    setLoading(true);
    setApiError('');
    setSuccessMessage('');

    const payload = {
      email: form.email.trim().toLowerCase(),
      password: form.password,
      role: form.role,
      dpdp_consent: form.dpdp_consent,
    };
    if (form.mobile.trim()) payload.mobile = form.mobile.trim();
    if (form.pan_number.trim()) payload.pan_number = form.pan_number.trim().toUpperCase();
    if (form.gst_number.trim()) payload.gst_number = form.gst_number.trim().toUpperCase();

    try {
      const { data } = await apiClient.post('/api/v1/auth/register/', payload);
      if (data.access) {
        setTokens({ access: data.access });
      }
      persistSessionMetadata({
        role: data.role || form.role,
        account_status: 'REGISTERED',
        kyc_complete: false,
        anonymous_id: data.anonymous_id,
      });

      setSuccessMessage(
        data.message || 'Account created! Please check your email to verify your address before logging in.'
      );
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setApiError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .ce-auth-container {
          display: flex;
          min-height: 100vh;
          background: #080d1a;
          color: #ffffff;
        }
        .ce-auth-left {
          flex: 1.1;
          position: relative;
          background: linear-gradient(135deg, #0d1526 0%, #080d1a 100%);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 60px;
          overflow: hidden;
          border-right: 1px solid rgba(255,255,255,0.05);
        }
        .ce-auth-left-bg {
          position: absolute;
          inset: 0;
          background-image: url('/auth-panel.png');
          background-size: cover;
          background-position: center;
          opacity: 0.18;
          mix-blend-mode: color-dodge;
          pointer-events: none;
        }
        .ce-auth-left-content {
          position: relative;
          z-index: 2;
          max-width: 500px;
          margin: auto 0;
        }
        .ce-auth-left-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 24px;
          font-weight: 700;
          color: #ffffff;
          cursor: pointer;
          user-select: none;
          text-decoration: none;
        }
        .ce-auth-left-logo-x {
          background: linear-gradient(135deg, #818cf8 0%, #06b6d4 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ce-auth-left-title {
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(28px, 4vw, 42px);
          font-weight: 700;
          line-height: 1.15;
          margin: 30px 0 16px;
          color: #ffffff;
          letter-spacing: -0.03em;
        }
        .ce-auth-left-desc {
          font-size: 18px;
          color: #f8fafc;
          line-height: 1.6;
          font-weight: 500;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
        }
        .ce-auth-bullets {
          display: flex;
          flex-direction: column;
          gap: 22px;
          margin-top: 40px;
        }
        .ce-auth-bullet {
          display: flex;
          align-items: flex-start;
          gap: 14px;
        }
        .ce-auth-bullet-icon {
          background: rgba(99, 102, 241, 0.22);
          border: 1px solid rgba(99, 102, 241, 0.45);
          border-radius: 10px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a5b4fc;
          flex-shrink: 0;
          box-shadow: 0 0 12px rgba(99,102,241,0.25);
        }
        .ce-auth-bullet-text h4 {
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 4px;
        }
        .ce-auth-bullet-text p {
          font-size: 15px;
          color: #e2e8f0;
          line-height: 1.5;
          font-weight: 500;
        }
        .ce-auth-left-foot {
          font-size: 13px;
          color: #64748b;
          z-index: 2;
          font-weight: 500;
        }
        .ce-auth-right {
          flex: 0.9;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 60px 40px;
          background: #080d1a;
          position: relative;
          z-index: 2;
          overflow-y: auto;
          max-height: 100vh;
        }
        .ce-auth-right::-webkit-scrollbar {
          width: 5px;
        }
        .ce-auth-right::-webkit-scrollbar-thumb {
          background: rgba(99, 102, 241, 0.2);
          border-radius: 4px;
        }
        .ce-auth-form-card {
          width: 100%;
          max-width: 480px;
          background: rgba(13, 21, 38, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          padding: 40px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.45);
        }
        .ce-auth-hd {
          text-align: center;
          margin-bottom: 28px;
        }
        .ce-auth-hd h2 {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 28px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 6px;
        }
        .ce-auth-hd p {
          font-size: 15px;
          color: #cbd5e1;
          font-weight: 500;
        }
        .ce-auth-inputs {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .ce-input-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .ce-input select.ce-input {
          background: transparent;
          color: #f1f5f9;
        }
        .ce-input select option {
          background: #0d1526;
          color: #f1f5f9;
        }
        .ce-auth-footer {
          text-align: center;
          margin-top: 24px;
          font-size: 14.5px;
          color: #cbd5e1;
          font-weight: 500;
        }
        .ce-auth-link {
          color: #818cf8;
          text-decoration: none;
          font-weight: 700;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          font-size: 14.5px;
          margin-left: 4px;
        }
        .ce-auth-link:hover {
          color: #a5b4fc;
          text-decoration: underline;
        }

        @media (max-width: 1024px) {
          .ce-auth-container {
            flex-direction: column;
          }
          .ce-auth-left {
            display: none;
          }
          .ce-auth-right {
            min-height: 100vh;
            padding: 30px 16px;
            overflow-y: visible;
            max-height: none;
          }
          .ce-auth-form-card {
            padding: 32px 20px;
          }
        }
      `}</style>

      <div className="ce-auth-container">
        {/* Left Side: Illustration Panel */}
        <div className="ce-auth-left">
          <div className="ce-auth-left-bg" />

          {/* Top Logo */}
          <a className="ce-auth-left-logo" href="/">
            <ShieldIcon className="ce-auth-left-logo-x" size={26} />
            <span>Cyber<span className="ce-auth-left-logo-x">Exchange</span></span>
          </a>

          {/* Central Bullet Highlights */}
          <div className="ce-auth-left-content">
            <h2 className="ce-auth-left-title">India's Premiere Anonymous B2B Cybersecurity Hub</h2>
            <p className="ce-auth-left-desc">
              Facilitating secure, compliant marketplace transactions without exposing private metadata.
            </p>

            <div className="ce-auth-bullets">
              <div className="ce-auth-bullet">
                <div className="ce-auth-bullet-icon">
                  <UserIcon size={19} />
                </div>
                <div className="ce-auth-bullet-text">
                  <h4>Cryptographic Identity Shielding</h4>
                  <p>Identities and technical proposals stay masked until digital contract signing.</p>
                </div>
              </div>
              <div className="ce-auth-bullet">
                <div className="ce-auth-bullet-icon">
                  <GlobeIcon size={19} />
                </div>
                <div className="ce-auth-bullet-text">
                  <h4>Secure Registry KYC Checks</h4>
                  <p>Instant compliance verification across business and corporate registries.</p>
                </div>
              </div>
              <div className="ce-auth-bullet">
                <div className="ce-auth-bullet-icon">
                  <ScaleIcon size={19} />
                </div>
                <div className="ce-auth-bullet-text">
                  <h4>DPDP Act &amp; Escrow Gating</h4>
                  <p>Milestone-gated payments paired with consent-driven secure audit logs.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer build copyright */}
          <div className="ce-auth-left-foot">
            <span>© 2026 CyberExchange · SmartX Technologies</span>
          </div>
        </div>

        {/* Right Side: Form Panel */}
        <div className="ce-auth-right">
          <div className="ce-auth-form-card ce-anim-scale-in">
            {/* Header */}
            <div className="ce-auth-hd">
              <h2>Create Account</h2>
              <p>Join the Cybersecurity Exchange Marketplace</p>
            </div>

            {/* Success message */}
            {successMessage && (
              <div className="ce-alert ce-alert-success" style={{ marginBottom: '20px' }}>
                <CheckIcon className="ce-alert-icon" size={18} />
                <div>{successMessage}</div>
              </div>
            )}

            {/* API error */}
            {apiError && (
              <div className="ce-alert ce-alert-error" style={{ marginBottom: '20px' }}>
                <AlertIcon className="ce-alert-icon" size={18} />
                <div>{apiError}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="ce-auth-inputs">
                
                {/* Role selection dropdown */}
                <div className="ce-input-wrap">
                  <label htmlFor="role" className="ce-label" style={{ color: '#ffffff' }}>ROLE *</label>
                  <div className="ce-input-icon-wrap">
                    <ScaleIcon className="ce-input-icon" size={16} />
                    <select
                      id="role"
                      name="role"
                      className="ce-input"
                      value={form.role}
                      onChange={handleChange}
                      style={{ appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}
                    >
                      <option value="BUYER" style={{ color: '#1a1a2e', background: '#ffffff' }}>Buyer — Post cybersecurity requirements</option>
                      <option value="BIDDER" style={{ color: '#1a1a2e', background: '#ffffff' }}>Bidder — Offer services &amp; submit proposals</option>
                      <option value="ARBITRATOR" style={{ color: '#1a1a2e', background: '#ffffff' }}>Arbitrator — Dispute resolution expert</option>
                    </select>
                  </div>
                  {fieldErrors.role && (
                    <span className="ce-input-error-msg">{fieldErrors.role}</span>
                  )}
                </div>

                {/* Email input */}
                <div className="ce-input-wrap">
                  <label htmlFor="email" className="ce-label" style={{ color: '#ffffff' }}>EMAIL ADDRESS *</label>
                  <div className="ce-input-icon-wrap">
                    <MailIcon className="ce-input-icon" size={16} />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="e.g. security@company.com"
                      className={`ce-input${fieldErrors.email ? ' ce-input-error' : ''}`}
                      value={form.email}
                      onChange={handleChange}
                      required
                      autoFocus
                    />
                  </div>
                  {fieldErrors.email && (
                    <span className="ce-input-error-msg">{fieldErrors.email}</span>
                  )}
                </div>

                {/* Password input */}
                <div className="ce-input-wrap">
                  <label htmlFor="password" className="ce-label" style={{ color: '#ffffff' }}>PASSWORD *</label>
                  <div className="ce-input-icon-wrap">
                    <LockIcon className="ce-input-icon" size={16} />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      className={`ce-input${fieldErrors.password ? ' ce-input-error' : ''}`}
                      value={form.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="ce-input-eye"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                    </button>
                  </div>
                  <span style={{ fontSize: '11px', color: '#a5b4fc', marginTop: '4px', fontWeight: '500' }}>
                    Min 8 characters, 1 uppercase letter, 1 number
                  </span>
                  {fieldErrors.password && (
                    <span className="ce-input-error-msg">{fieldErrors.password}</span>
                  )}
                </div>

                {/* Confirm password input */}
                <div className="ce-input-wrap">
                  <label htmlFor="confirmPassword" className="ce-label" style={{ color: '#ffffff' }}>CONFIRM PASSWORD *</label>
                  <div className="ce-input-icon-wrap">
                    <ShieldIcon className="ce-input-icon" size={16} />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      className={`ce-input${fieldErrors.confirmPassword ? ' ce-input-error' : ''}`}
                      value={form.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="ce-input-eye"
                      onClick={() => setShowConfirm(!showConfirm)}
                      aria-label="Toggle password visibility"
                    >
                      {showConfirm ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && (
                    <span className="ce-input-error-msg">{fieldErrors.confirmPassword}</span>
                  )}
                </div>

                {/* DPDP Act consent checkbox */}
                <div className="ce-input-wrap" style={{ marginTop: '8px' }}>
                  <label className="ce-checkbox-wrap">
                    <input
                      name="dpdp_consent"
                      type="checkbox"
                      className="ce-checkbox"
                      checked={form.dpdp_consent}
                      onChange={handleChange}
                    />
                    <span style={{ fontSize: '13px', color: '#e2e8f0', lineHeight: '1.45', fontWeight: '500' }}>
                      I consent to the collection and secure processing of my details under the{' '}
                      <strong style={{ color: '#ffffff' }}>Digital Personal Data Protection Act (DPDP) 2023</strong>.
                      My details stay shielded during bid matching.
                    </span>
                  </label>
                  {fieldErrors.dpdp_consent && (
                    <span className="ce-input-error-msg" style={{ marginLeft: '30px' }}>
                      {fieldErrors.dpdp_consent}
                    </span>
                  )}
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="ce-btn ce-btn-primary ce-btn-full"
                  style={{ marginTop: '12px', padding: '14px' }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="ce-spinner" />
                      <span>Creating Account...</span>
                    </>
                  ) : (
                    <span>Create Account</span>
                  )}
                </button>

              </div>
            </form>

            {/* Back to sign in link */}
            <div className="ce-auth-footer">
              <span>Already have an account?</span>
              <button className="ce-auth-link" onClick={() => navigate('/login')}>
                Sign in here
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
